/**
 * Supabase backend factory (APP_BACKEND=supabase). Returns the adapters the composition root swaps in; nothing here runs unless the
 * build selected the live backend (see src/config/backend.ts), so the default local demo is byte-for-byte unaffected.
 */
import type { UseCases } from '../../domain/services';
import type { Locale } from '../../domain/types';
import type { LiveBackend } from '../index';
import type { Clock, KeyValueStore } from '../types';
import { createSupabaseAuth } from './auth';
import { createOutboxMessaging } from './messaging';
import { createClientPayments } from './payments';
import { createFetchRest } from './rest';
import { PHOTO_BUCKET, SupabaseStorage } from './storage';
import { createRealClock } from './clock';
import { createLiveUseCases } from './usecases';
import { createDeviceRegistrar } from './devices';

export interface SupabaseConfig { url: string; anonKey: string; pollMs?: number }

export function createSupabaseBackend(config: SupabaseConfig, ctx: { kv: KeyValueStore; locale: () => Locale; fetch?: typeof fetch }): LiveBackend {
  const clock = createRealClock();
  const base = config.url.replace(/\/+$/, '');
  // eslint-disable-next-line prefer-const
  let auth: ReturnType<typeof createSupabaseAuth>;
  const rest = createFetchRest({ url: base, anonKey: config.anonKey, getToken: () => auth.getAccessToken(), ...(ctx.fetch ? { fetch: ctx.fetch } : {}) });
  auth = createSupabaseAuth({ rest, kv: ctx.kv, clock, authUrl: `${base}/auth/v1`, anonKey: config.anonKey, ...(ctx.fetch ? { fetch: ctx.fetch } : {}) });
  const storage = new SupabaseStorage({ rest, signedIn: () => auth.getAccessToken() !== undefined, ctx: { mode: 'client', photoUrl: (p) => rest.storage.publicUrl(PHOTO_BUCKET, p) } });
  const messaging = createOutboxMessaging({ storage, clock, locale: ctx.locale });
  const payments = createClientPayments({ storage });
  const devices = createDeviceRegistrar({ rest, auth, locale: ctx.locale });
  let stopRefresh: (() => void) | undefined;
  return {
    kind: 'supabase', storage, messaging, payments, auth, clock, devices,
    wrapUseCases: (app: UseCases): UseCases => createLiveUseCases(app, rest, { auth, afterOp: () => void storage.pollOnce().catch(() => undefined) }),
    async start() {
      if (auth.getSession() === undefined) await auth.refresh();   // an expired access token may still have a valid refresh token
      stopRefresh = auth.autoRefresh();
      await storage.pollOnce().catch(() => undefined);              // baseline versions
      storage.startPolling(config.pollMs ?? 15_000);
    },
    stop() { stopRefresh?.(); storage.stopPolling(); },
  };
}
